import {
  getRoleConfig,
  getRatingSet,
  POSITION_NAMES,
} from "../../pages/EvaluationNew/evaluation.constants";
import { mapBackendDataToFrontend } from "../../pages/EvaluationNew/payloadMappings";
import {
  PROTOCOL_DOCUMENT_PRINT_STYLES,
  escapeHtml,
  formatDate,
  signatureBlock,
} from "./jobDescriptionPrint";

const EVAL_PRINT_EXTRA = `
  .ev-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; margin-bottom: 16px; }
  .ev-meta-item { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; }
  .ev-meta-lbl { font-size: 9px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  .ev-meta-val { font-size: 11px; font-weight: 600; color: #111827; margin-top: 4px; }
  .ev-key { display: flex; flex-wrap: wrap; gap: 8px 14px; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; margin: 12px 0 16px; font-size: 10px; }
  .ev-section { margin-top: 18px; page-break-inside: auto; }
  .ev-section h2 { font-size: 12px; font-weight: 800; color: #1e3a8a; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; margin: 0 0 10px 0; }
  .ev-table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 8px; }
  .ev-table th, .ev-table td { border: 1px solid #e5e7eb; padding: 6px 8px; vertical-align: top; }
  .ev-table th { background: #f3f4f6; text-align: left; font-weight: 700; }
  .ev-q { width: 78%; text-align: justify; }
  .ev-r { width: 22%; text-align: center; font-weight: 700; white-space: nowrap; }
  .ev-sub { margin: 4px 0 0 12px; font-size: 9.5px; color: #374151; }
  .ev-sub-lbl { font-weight: 600; }
  .ev-comments { margin-top: 10px; padding: 10px; background: #fafafa; border-radius: 8px; border: 1px solid #e5e7eb; font-size: 10px; }
  .ev-comments h4 { margin: 0 0 6px 0; font-size: 10px; font-weight: 700; color: #374151; }
  .ev-page-break { page-break-before: always; padding-top: 14px; }
`;

/**
 * @param {string|undefined|null} role
 */
export function normalizeEvaluationRole(role) {
  const r = String(role ?? "")
    .trim()
    .toUpperCase();
  const alias = {
    "OFFICE ASSISTANT": "OA",
    "OFFICE-ASSISTANT": "OA",
  };
  return alias[r] || r;
}

/**
 * API base path for GET/PUT by id (same as {@link useEvaluationForm} load).
 * @param {string|undefined|null} role
 */
export function getEvaluationFetchEndpoint(role) {
  return getRoleConfig(normalizeEvaluationRole(role)).apiEndpoint;
}

/**
 * @param {{ status?: number; result?: unknown }} response
 */
export function parseEvaluationFromApiResponse(response) {
  if (!response || response.status !== 1) return null;
  const r = response.result;
  if (r?.evaluation && typeof r.evaluation === "object") {
    return /** @type {Record<string, unknown>} */ (r.evaluation);
  }
  if (r && typeof r === "object" && !Array.isArray(r) && r.id != null) {
    return /** @type {Record<string, unknown>} */ (r);
  }
  return null;
}

/**
 * @param {string} role
 * @param {string|number} evaluationId
 * @param {(path: string) => Promise<{ status?: number; result?: unknown }>} requestFn
 */
export async function fetchEvaluationForPrint(role, evaluationId, requestFn) {
  const endpoint = getEvaluationFetchEndpoint(role);
  const response = await requestFn(`${endpoint}/${evaluationId}`);
  const backend = parseEvaluationFromApiResponse(response);
  if (!backend) return null;
  const norm = normalizeEvaluationRole(role);
  const mapped = mapBackendDataToFrontend(norm, backend);
  return { backend, mapped, role: norm };
}

/**
 * @param {unknown} item
 * @param {Record<string, unknown>} mapped
 */
function dutyItemRows(item, mapped) {
  if (!item || typeof item !== "object") return "";
  const it = /** @type {{ id?: string; text?: string; subItems?: { id?: string; label?: string }[] }} */ (
    item
  );
  const id = it.id;
  const rating =
    id && mapped[id] != null && mapped[id] !== ""
      ? escapeHtml(String(mapped[id]))
      : "—";
  let subHtml = "";
  if (Array.isArray(it.subItems) && it.subItems.length > 0) {
    subHtml = it.subItems
      .map((si) => {
        const sid = si.id;
        const v =
          sid && mapped[sid] != null && mapped[sid] !== ""
            ? escapeHtml(String(mapped[sid]))
            : "—";
        return `<div class="ev-sub"><span class="ev-sub-lbl">${escapeHtml(si.label || sid || "")}:</span> ${v}</div>`;
      })
      .join("");
  }
  const subRow = subHtml
    ? `<tr><td colspan="2" style="border-top:none;padding-top:0">${subHtml}</td></tr>`
    : "";
  return `<tr>
    <td class="ev-q">${escapeHtml(it.text || "")}</td>
    <td class="ev-r">${rating}</td>
  </tr>${subRow}`;
}

/**
 * @param {string} roleNorm
 * @param {Record<string, unknown>} mapped
 */
function buildDutiesBodyHtml(roleNorm, mapped) {
  const cfg = getRoleConfig(roleNorm);
  const chunks = [];

  if (cfg.pages && Array.isArray(cfg.pages)) {
    cfg.pages.forEach((pageBlock, pi) => {
      const inner = pageBlock
        .map((section) => {
          const title = escapeHtml(section.title || "");
          const rows = (section.items || [])
            .map((item) => dutyItemRows(item, mapped))
            .join("");
          return `<div class="ev-section ${pi > 0 ? "ev-page-break" : ""}">
            <h2>${title}</h2>
            <table class="ev-table"><thead><tr><th>Standard / duty</th><th>Rating</th></tr></thead><tbody>${rows}</tbody></table>
          </div>`;
        })
        .join("");
      chunks.push(inner);
    });
    return chunks.join("");
  }

  if (cfg.duties && Array.isArray(cfg.duties)) {
    return cfg.duties
      .map((block, bi) => {
        const title = escapeHtml(block.title || "Essential duties");
        const rows = (block.items || [])
          .map((item) => dutyItemRows(item, mapped))
          .join("");
        return `<div class="ev-section ${bi > 0 ? "ev-page-break" : ""}">
          <h2>${title}</h2>
          <table class="ev-table"><thead><tr><th>Standard / duty</th><th>Rating</th></tr></thead><tbody>${rows}</tbody></table>
        </div>`;
      })
      .join("");
  }

  return `<p class="jd-muted">No duty layout is configured for this role.</p>`;
}

/**
 * @param {string} roleNorm
 * @param {Record<string, unknown>} mapped
 * @param {Record<string, unknown>} backend
 */
function buildMetaCommentsHtml(roleNorm, mapped, backend) {
  const cfg = getRoleConfig(roleNorm);
  const jobKey = roleNorm.toLowerCase();
  const jobTitle =
    escapeHtml(
      String(
        mapped.jobTitle ||
          POSITION_NAMES[jobKey] ||
          POSITION_NAMES[jobKey.replace(/\s+/g, "-")] ||
          roleNorm,
      ),
    ) || escapeHtml(roleNorm);

  const emp =
    mapped.employeeLabel ||
    (backend.employee &&
      `${/** @type {{ firstName?: string }} */ (backend.employee).firstName || ""} ${/** @type {{ lastName?: string }} */ (backend.employee).lastName || ""}`.trim()) ||
    "";

  const metaRows = [
    ["Position / form", jobTitle],
    ["Employee", emp ? escapeHtml(String(emp)) : "—"],
    [
      "Evaluation date",
      mapped.evaluationDate
        ? escapeHtml(formatDate(mapped.evaluationDate) || String(mapped.evaluationDate))
        : "—",
    ],
    [
      "Hire date",
      mapped.hireDate
        ? escapeHtml(formatDate(mapped.hireDate) || String(mapped.hireDate))
        : "—",
    ],
    [
      "Review type",
      escapeHtml(String(mapped.reviewType || mapped.evaluationType || "—")),
    ],
    [
      "Evaluator",
      escapeHtml(
        String(mapped.evaluatorName || mapped.reviewerName || "—"),
      ),
    ],
    ["Days tardy", escapeHtml(String(mapped.daysTardy || "—"))],
    ["Days absent", escapeHtml(String(mapped.daysAbsent || "—"))],
  ];

  const metaHtml = metaRows
    .map(
      ([k, v]) => `
    <div class="ev-meta-item">
      <div class="ev-meta-lbl">${escapeHtml(k)}</div>
      <div class="ev-meta-val">${v}</div>
    </div>`,
    )
    .join("");

  const ratingSet = getRatingSet(roleNorm);
  const keyHtml = ratingSet
    .map(
      (x) =>
        `<span><strong>${escapeHtml(x.value)}</strong> = ${escapeHtml(x.label)}</span>`,
    )
    .join("");

  let commentsHtml = "";
  const commentParts = [];
  for (let p = 1; p <= 3; p++) {
    const k = `dutiesCommentPage${p}`;
    const t = mapped[k];
    if (t && String(t).trim()) {
      commentParts.push(
        `<div><strong>Comments (part ${p})</strong><p style="margin:4px 0 0 0;white-space:pre-wrap">${escapeHtml(String(t))}</p></div>`,
      );
    }
  }
  if (mapped.overallComments && String(mapped.overallComments).trim()) {
    commentParts.push(
      `<div><strong>Overall comments</strong><p style="margin:4px 0 0 0;white-space:pre-wrap">${escapeHtml(String(mapped.overallComments))}</p></div>`,
    );
  }
  if (cfg.hasTargetedGoals) {
    for (let p = 1; p <= 3; p++) {
      const gk = `targetedGoalsPage${p}`;
      const g = mapped[gk];
      if (g && String(g).trim()) {
        commentParts.push(
          `<div><strong>Targeted goals (part ${p})</strong><p style="margin:4px 0 0 0;white-space:pre-wrap">${escapeHtml(String(g))}</p></div>`,
        );
      }
    }
  }
  if (commentParts.length) {
    commentsHtml = `<div class="ev-comments"><h4>Comments &amp; goals</h4>${commentParts.join("")}</div>`;
  }

  return `<div class="ev-meta">${metaHtml}</div>
    <div class="ev-key">${keyHtml}</div>
    ${commentsHtml}`;
}

/**
 * @param {string} roleNorm — normalized role (RN, PT, …)
 * @param {Record<string, unknown>} mapped — output of mapBackendDataToFrontend
 * @param {Record<string, unknown>} backend — raw API evaluation object
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildEvaluationPrintHtml(roleNorm, mapped, backend, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");
  const roleDisp = escapeHtml(roleNorm);

  const dutiesHtml = buildDutiesBodyHtml(roleNorm, mapped);
  const metaBlock = buildMetaCommentsHtml(roleNorm, mapped, backend);

  const reviewerSig =
    mapped.reviewerSignature ||
    mapped.reviewerSignatureImage ||
    backend.reviewerSignature ||
    backend.adminSignature;
  const reviewerDateRaw =
    mapped.reviewerSignedAt ||
    mapped.reviewerSignatureDate ||
    backend.reviewerSignedAt ||
    backend.adminSignatureDate;
  const reviewerDate = escapeHtml(formatDate(reviewerDateRaw) || "");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Performance evaluation (${roleDisp}) — ${company}</title>
  <style>${PROTOCOL_DOCUMENT_PRINT_STYLES}${EVAL_PRINT_EXTRA}</style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Performance evaluation</p>
        <p class="title-sub">${roleDisp} · ${company}</p>
      </div>
    </div>

    ${metaBlock}
    ${dutiesHtml}

    <div class="sig-card" style="margin-top:20px">
      <h3>Reviewer / administrator</h3>
      <div class="sig-label">Name</div>
      <div class="field-value">${escapeHtml(String(mapped.reviewerName || mapped.evaluatorName || "—"))}</div>
      <div class="sig-label">Signature</div>
      <div class="sig-line">${signatureBlock(reviewerSig)}</div>
      <div class="sig-label">Date</div>
      <div class="sig-line">${reviewerDate || "—"}</div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
